import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import PhotoUploader from "@/components/PhotoUploader";
import Select from "@/components/admin/Select";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save } from "lucide-react";

const EMPTY = {
  type: "auto",
  condition: "Usato",
  brand: "",
  model: "",
  price: "",
  year: new Date().getFullYear(),
  km: 0,
  fuel: "Benzina",
  color: "",
  transmission: "Manuale",
  power: "",
  images: [],
  description: "",
};

const TYPE_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "scooter", label: "Scooter" },
  { value: "scooter_cabinato", label: "Scooter cabinato" },
  { value: "moto", label: "Moto" },
];
const CONDITION_OPTIONS = [
  { value: "Nuovo", label: "Nuovo" },
  { value: "Usato", label: "Usato" },
];
const FUEL_OPTIONS = ["Benzina", "Diesel", "Ibrida", "Elettrica"].map((f) => ({ value: f, label: f }));
const TRANS_OPTIONS = ["Manuale", "Automatico"].map((t) => ({ value: t, label: t }));

export default function AdminVehicleForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const { data } = await api.get(`/vehicles/${id}`);
        setForm({ ...EMPTY, ...data });
      } catch (err) {
        toast.error(formatApiError(err));
        navigate("/admin");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, editing, navigate]);

  const upd = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]:
        e.target.type === "number"
          ? e.target.value === ""
            ? ""
            : Number(e.target.value)
          : e.target.value,
    }));

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onImagesChange = (images) => setForm((f) => ({ ...f, images }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        year: Number(form.year) || new Date().getFullYear(),
        km: Number(form.km) || 0,
      };
      if (editing) {
        await api.put(`/vehicles/${id}`, payload);
        toast.success("Veicolo aggiornato");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Veicolo creato");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 className="animate-spin mr-3" /> Caricamento…
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#76BA1B] mb-5"
      >
        <ArrowLeft size={14} /> Torna alla lista
      </Link>

      <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1e293b] mb-1">
        {editing ? "Modifica veicolo" : "Nuovo veicolo"}
      </h1>
      <p className="text-sm text-slate-500 mb-8">
        {editing ? "Aggiorna i dati e le foto del veicolo." : "Inserisci tutti i dati del veicolo e allega le foto."}
      </p>

      <form
        onSubmit={submit}
        data-testid="admin-vehicle-form"
        className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200 shadow-[0_10px_30px_rgba(15,23,42,0.04)] space-y-6"
      >
        {/* Photos section first — most-used UI */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1 h-5 rounded-full bg-[#76BA1B]" />
            <h2 className="font-heading text-lg font-bold text-[#1e293b]">Foto del veicolo</h2>
          </div>
          <PhotoUploader value={form.images || []} onChange={onImagesChange} />
        </div>

        <div className="border-t border-slate-100" />

        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-5 rounded-full bg-[#76BA1B]" />
            <h2 className="font-heading text-lg font-bold text-[#1e293b]">Dati principali</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Tipologia *">
              <Select
                testid="vform-type"
                value={form.type}
                onChange={setField("type")}
                options={TYPE_OPTIONS}
                placeholder="Seleziona tipologia"
                required
              />
            </Field>
            <Field label="Condizione *">
              <Select
                testid="vform-condition"
                value={form.condition || ""}
                onChange={setField("condition")}
                options={CONDITION_OPTIONS}
                placeholder="Seleziona condizione"
                required
              />
            </Field>
            <Field label="Marca *">
              <input required data-testid="vform-brand" value={form.brand} onChange={upd("brand")} className={inputCls} placeholder="Es. Fiat" />
            </Field>
            <Field label="Modello *">
              <input required data-testid="vform-model" value={form.model} onChange={upd("model")} className={inputCls} placeholder="Es. 500 1.2 Lounge" />
            </Field>
            <Field label="Prezzo (€) *">
              <input required type="number" min="0" step="100" data-testid="vform-price" value={form.price} onChange={upd("price")} className={inputCls} placeholder="9800" />
            </Field>
            <Field label="Anno *">
              <input required type="number" min="1980" max={new Date().getFullYear() + 1} data-testid="vform-year" value={form.year} onChange={upd("year")} className={inputCls} />
            </Field>
            <Field label="Chilometri *">
              <input required type="number" min="0" data-testid="vform-km" value={form.km} onChange={upd("km")} className={inputCls} />
            </Field>
            <Field label="Alimentazione *">
              <Select
                testid="vform-fuel"
                value={form.fuel}
                onChange={setField("fuel")}
                options={FUEL_OPTIONS}
                placeholder="Seleziona alimentazione"
                required
              />
            </Field>
            <Field label="Cambio">
              <Select
                testid="vform-transmission"
                value={form.transmission || ""}
                onChange={setField("transmission")}
                options={TRANS_OPTIONS}
                placeholder="Seleziona cambio"
              />
            </Field>
            <Field label="Colore">
              <input data-testid="vform-color" value={form.color || ""} onChange={upd("color")} className={inputCls} placeholder="Es. Bianco Perla" />
            </Field>
            <Field label="Potenza">
              <input data-testid="vform-power" value={form.power || ""} onChange={upd("power")} className={inputCls} placeholder="Es. 95 CV" />
            </Field>
          </div>
        </div>

        <Field label="Descrizione">
          <textarea
            rows={4}
            data-testid="vform-description"
            value={form.description || ""}
            onChange={upd("description")}
            className={inputCls + " resize-none"}
            placeholder="Tagliandi certificati, garanzia inclusa…"
          />
        </Field>

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-slate-100">
          <Link to="/admin" className="px-6 py-3 rounded-xl border border-slate-200 text-slate-600 text-sm hover:border-slate-300 hover:bg-slate-50 text-center transition">
            Annulla
          </Link>
          <button
            type="submit"
            disabled={saving}
            data-testid="vform-submit"
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-[#76BA1B] text-white font-bold hover:bg-[#5fa015] hover:shadow-[0_8px_28px_rgba(118,186,27,0.45)] disabled:opacity-60 transition"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? "Salvataggio…" : editing ? "Salva modifiche" : "Crea veicolo"}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}

const inputCls =
  "w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-sm text-[#1e293b] placeholder-slate-400 focus:border-[#76BA1B] focus:shadow-[0_0_0_4px_rgba(118,186,27,0.12)] outline-none transition";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 font-bold">
        {label}
      </div>
      {children}
    </label>
  );
}
